// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  MessageSquare, Users, Clock, TrendingUp, BarChart3,
} from 'lucide-react';
import { getChatHistory } from '../../services/api';

interface ChatbotStatsTabProps {
  clientId: string;
}

const ChatbotStatsTab: React.FC<ChatbotStatsTabProps> = ({ clientId }) => {
  const [stats, setStats] = useState({
    totalConversations: 0,
    totalMessages: 0,
    avgResponseTime: '< 2s',
    activeToday: 0,
  });
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<any[]>([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (clientId) {
          const history = await getChatHistory(clientId, undefined, 100);
          const sessions = history?.sessions || history?.data || [];
          setConversations(sessions);

          const today = new Date().toDateString();
          const activeToday = sessions.filter((s: any) =>
            new Date(s.created_at || s.timestamp).toDateString() === today
          ).length;

          const totalMessages = sessions.reduce((sum: number, s: any) =>
            sum + (s.message_count || s.messages?.length || 0), 0
          );

          setStats({
            totalConversations: sessions.length,
            totalMessages,
            avgResponseTime: '< 2s',
            activeToday,
          });
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [clientId]);

  const statCards = [
    { icon: MessageSquare, label: 'Total Conversations', value: stats.totalConversations, color: 'brand' },
    { icon: Users, label: 'Messages Exchanged', value: stats.totalMessages, color: 'indigo' },
    { icon: TrendingUp, label: 'Active Today', value: stats.activeToday, color: 'emerald' },
    { icon: Clock, label: 'Avg Response Time', value: stats.avgResponseTime, color: 'amber' },
  ];

  const colorMap: Record<string, { bg: string; icon: string }> = {
    brand:   { bg: 'bg-brand-50', icon: 'text-brand-500' },
    indigo:  { bg: 'bg-indigo-50', icon: 'text-indigo-500' },
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-500' },
    amber:   { bg: 'bg-amber-50', icon: 'text-amber-500' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          const c = colorMap[card.color] || colorMap.brand;
          return (
            <div key={card.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-center gap-4">
                <div className={`w-11 h-11 rounded-xl ${c.bg} flex items-center justify-center shrink-0`}>
                  <Icon size={22} className={c.icon} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{card.value}</div>
                  <div className="text-sm text-gray-500 font-medium">{card.label}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent conversations */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Recent Conversations</h3>
          <span className="text-xs text-gray-400">{conversations.length} total</span>
        </div>
        <div className="divide-y divide-gray-100">
          {conversations.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <BarChart3 size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No conversations yet. Deploy your chatbot to start collecting data.</p>
            </div>
          ) : (
            conversations.slice(0, 10).map((conv: any, idx: number) => (
              <div key={conv.session_id || idx} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <Users size={14} className="text-gray-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {conv.user_name || conv.visitor_id || `Visitor ${idx + 1}`}
                    </div>
                    <div className="text-xs text-gray-400">
                      {conv.message_count || conv.messages?.length || 0} messages
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-400 shrink-0">
                  {conv.created_at || conv.timestamp
                    ? new Date(conv.created_at || conv.timestamp).toLocaleDateString()
                    : ''}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatbotStatsTab;
